import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Plus, FileText, Trash2, Copy } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

function Tests() {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isFaculty = user.role === 'faculty';

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      const response = await api.get('/tests');
      setTests(response.data);
    } catch (error) {
      console.error('Error fetching tests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (testId, testTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${testTitle}"? This will also delete all associated attempts and cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/tests/${testId}`);
      toast.success('Test deleted successfully');
      fetchTests();
    } catch (error) {
      console.error('Error deleting test:', error);
      toast.error('Failed to delete test');
    }
  };

  const copyTestId = (testId, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(testId);
    toast.success('Test ID copied to clipboard!');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="tests-title">Tests</h1>
            <p className="text-gray-600">Manage your assessments</p>
          </div>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => navigate('/admin/tests/create')}
            data-testid="create-test-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Test
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tests.map((test) => (
            <Card
              key={test.id}
              className="p-6 card-hover relative"
              data-testid={`test-card-${test.id}`}
            >
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 text-red-500 hover:text-red-700 hover:bg-red-50"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(test.id, test.title);
                }}
                data-testid={`delete-test-${test.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              <div 
                className="cursor-pointer"
                onClick={() => navigate(`/admin/tests/${test.id}`)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                      <FileText className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900">{test.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={test.status === 'published' ? 'success' : 'secondary'}>
                          {test.status}
                        </Badge>
                        {!isFaculty && test.created_by_role === 'faculty' && test.created_by_name && (
                          <Badge className="bg-purple-100 text-purple-800 text-xs" data-testid={`test-creator-${test.id}`}>
                            Faculty: {test.created_by_name}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">{test.description}</p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{test.question_ids?.length || 0} questions</span>
                    <span className="text-gray-500">{test.config?.duration || 0} mins</span>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                    <span className="text-xs text-gray-400">Test ID:</span>
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono text-gray-700 flex-1 truncate">
                      {test.id}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2"
                      onClick={(e) => copyTestId(test.id, e)}
                      data-testid={`copy-test-id-${test.id}`}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {tests.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tests yet</h3>
            <p className="text-gray-500 mb-4">Create your first test to get started</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Tests;